import {isEqual} from "lodash";
import {Admission, ChecklistItem} from "@reside/reside-api-admission";

import {AdmissionFromSearch} from "../../services/AdmissionsService";
import {
  readResidentFirstNameAnswer,
  readResidentLastNameAnswer,
} from "../../models/AdmissionModel.helpers";
import {ChecklistValues} from "./atoms/checklist";

export const getAdmissionResidentName = (
  {answers}: Pick<Admission | AdmissionFromSearch, "answers">,
  separator = " ",
) =>
  [answers?.resident_firstName?.value, answers?.resident_lastName?.value].join(
    separator,
  );

export const getResidentTestId = (
  admission: Pick<Admission | AdmissionFromSearch, "answers">,
  elementId: string,
) =>
  `${readResidentFirstNameAnswer(admission as any)}${readResidentLastNameAnswer(
    admission as any,
  )}-${elementId}`;

/**
 * Whether the delete/cancel action is disabled for the current user, given the
 * per-facility "only-admin-can-delete" feature flags.
 *
 * Org admins are never restricted. The org flag is the stricter policy: when it is
 * on, only org admins may delete even if the facility flag is also on
 * (most-restrictive wins). The facility flag alone additionally allows facility
 * admins. With no flag set, deletion is unrestricted.
 */
export const isDeleteRestrictedForUser = ({
  facilityFlags,
  isOrgAdmin,
  isFacilityAdmin,
}: {
  facilityFlags?: {[key: string]: boolean};
  isOrgAdmin: boolean;
  isFacilityAdmin: boolean;
}) => {
  const orgAdminOnly = !!facilityFlags?.facilityHasOrgAdminOnlyDeleteAdmissions;
  const facilityAdminOnly =
    !!facilityFlags?.facilityHasFacilityAdminOnlyDeleteAdmissions;

  if (!orgAdminOnly && !facilityAdminOnly) {
    return false;
  }

  // Each flag restricts deletion to one admin role; when both are on the restrictions cancel
  // out and either admin role may delete.
  return !(
    (orgAdminOnly && isOrgAdmin) ||
    (facilityAdminOnly && isFacilityAdmin)
  );
};

export const getChecklistFormValues = (
  checklist: ReadonlyArray<ChecklistItem>,
) =>
  checklist.reduce<Record<string, ChecklistItem>>(
    (values, item) => ({
      ...values,
      [item.id]: item,
    }),
    {},
  );

export const getUpdatesAdmissionChecklist = (
  checklist: ReadonlyArray<ChecklistItem>,
  values: ChecklistValues,
) => {
  const currentValues = getChecklistFormValues(checklist);

  return Object.entries(currentValues)
    .filter(([id, item]) => !isEqual(item, values[id]))
    .map(([id]) => [id, values[id]] as [string, ChecklistItem]);
};
